import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Form } from "reactstrap";
import emojiToolkit from "emoji-toolkit";
import { MentionsInput, Mention } from 'react-mentions';
import classNames from "classnames";
import { uniq } from "underscore";

import Toast from "shared/toast";

import EmojiPicker from "./EmojiPicker";
import EmojiMentionSuggestion from "./EmojiMentionSuggestion";
import UserMentionSuggestion from "./UserMentionSuggestion";
import emojiFuzzyQuery from "./helpers/emojiFuzzyQuery";
import mentionsInputInlineStyle from "./styles/mentionsInputInlineStyle";
import { button, colorBlue50 } from "./styles/Comments.module.scss";

const CommentForm = ({
  buttonText,
  editing,
  hashid,
  initialText,
  onCancel,
  onSubmit,
  placeholder,
  userMentionsData,
  focus
}) => {
// The coverageThreshold.js entry for this file is low because it's *very* difficult to test
// the react-mentions library.
  const [text, setText] = useState(emojiToolkit.shortnameToUnicode(initialText));
  const [plainText, setPlainText] = useState(text);
  const [mentions, setMentions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [forceNewCursorPosition, setForceNewCursorPosition] = useState(-1);
  const [focusedSuggestion, setFocusedSuggestion] = useState();
  const [suggestedUserOptions, setSuggestedUserOptions] = useState([]);
  const [metaKeyPressed, setMetaKeyPressed] = useState(false);

  const resetDataOnSubmitComplete = () => {
    setText("");
    setPlainText("");
    setMentions([]);
  };

  const inputRef = useRef();
  const focusedSuggestionRef = useRef();

  useEffect(() => {
    if (forceNewCursorPosition >= 0) {
      inputRef.current.setSelectionRange(forceNewCursorPosition, forceNewCursorPosition);
      setForceNewCursorPosition(-1);
    }
  }, [forceNewCursorPosition, inputRef]);

  useEffect(() => {
    if (focusedSuggestionRef.current) {
      // We need to set focusedSuggestionRef so that a re-render occurs so this useEffect can be triggered to scroll
      // item into view. This is needed since we want to set a max height on the suggestions box.
      focusedSuggestionRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [focusedSuggestion]);

  useEffect(() => {
    if (focus) {
      inputRef.current.focus();
    }
  }, [focus]);

  const onTextChange = (event, newTextValue, newPlainTextValue, allMentions) => {
    const value = event.target.value;
    const originalLength = value.length;
    const valueInUnicode = emojiToolkit.shortnameToUnicode(value);
    const newLength = valueInUnicode.length;

    if (originalLength <= 50000) {
      if (originalLength !== newLength) {
        const cursorPositionFromEnd = originalLength - inputRef.current.selectionStart + 1;
        setForceNewCursorPosition(newLength - cursorPositionFromEnd);
      }
      // `text` stores text with formatting needed for "@" mentions, `plainText` stores what the user sees.
      setText(valueInUnicode);
      setPlainText(emojiToolkit.shortnameToUnicode(newPlainTextValue));
      setMentions(allMentions);
    }
  };

  const onSubmitShortcut = event => {
    if (event.key === "Enter" && (metaKeyPressed || event.ctrlKey)) { // event.metaKey is not registered in some browsers
      event.preventDefault();
      submit();
    }
  };

  const onKeyUp = event => {
    if (event.key === "Meta") {
      setMetaKeyPressed(false);
    }
  };

  const onKeyDown = event => {
    if (event.key === "Meta") {
      setMetaKeyPressed(true);
    } else {
      onSubmitShortcut(event);
    }
  };

  const addEmojiFromPicker = unicodeEmoji => {
    setText(text + unicodeEmoji + ' ');
    setPlainText(plainText + unicodeEmoji + ' ');
  };

  const userMentionsDataQuery = async (query, callback) => {
    if (query) {
      try {
        const mentionOptions = await userMentionsData(query);
        if (mentionOptions) {
          setSuggestedUserOptions(uniq(suggestedUserOptions.concat(mentionOptions)));
          callback(mentionOptions);
        }
      } catch (error) {}
    }
  };

  const focusedSuggestionRefParam = (focused, entryId) => {
    const ref = focused ? { ref: focusedSuggestionRef } : {};
    if (ref.ref) {
      setFocusedSuggestion(entryId);
    }
    return ref;
  };

  const renderEmojiSuggestion = (entry, _search, _highlightedDisplay, _index, focused) => {
    return <EmojiMentionSuggestion
      {...focusedSuggestionRefParam(focused, entry.id)}
      id={entry.id}
      display={entry.display}
      focused={focused}
    />;
  };

  const renderUserMentionSuggestion = (entry, _search, _highlightedDisplay, _index, focused) => {
    return <UserMentionSuggestion
      {...focusedSuggestionRefParam(focused, entry.id)}
      display={entry.display}
      focused={focused}
      email={entry.email}
      role={entry.role}
    />;
  };

  const translatedMentions = () => {
    return mentions.map(({ id, display, plainTextIndex }) => {
      const selectedOption = suggestedUserOptions.find(suggestedUserOption => suggestedUserOption.id === id);
      const email = selectedOption ? selectedOption.email : null;
      return { primaryKey: id, label: display, textIndex: plainTextIndex, email: email };
    });
  };

  const submit = async () => {
    if (text && text.trim()) {
      setSubmitting(true);
      const mentionsArg = mentions.length < 1 ? {} : { mentions: translatedMentions() };
      try {
        await onSubmit({ hashid: hashid, richText: emojiToolkit.toShort(plainText), ...mentionsArg });
        resetDataOnSubmitComplete();
      } catch (error) {
        Toast.error({
          title: "Error posting comment",
          message: "An error occurred while trying to post your comment. Please try again."
        });
      }
      setSubmitting(false);
    }
  };

  return (
    <Form className="mt-3 d-flex flex-column" onSubmit={submit}>
      <div className="d-flex">
        <div style={{ flex: 1 }}>
          <MentionsInput
            inputRef={inputRef}
            value={text}
            onChange={onTextChange}
            placeholder={placeholder}
            onKeyPress={onSubmitShortcut}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            style={mentionsInputInlineStyle}
          >
            <Mention
              trigger=":"
              data={emojiFuzzyQuery}
              renderSuggestion={renderEmojiSuggestion}
              markup="__display__"
              regex={/($a)/}
              appendSpaceOnAdd
            />
            {((!editing && userMentionsData) || null) && // MentionsInput can't accept `true` or `false` as children, so render `null` for `false`.
              <Mention
                trigger="@"
                data={userMentionsDataQuery}
                renderSuggestion={renderUserMentionSuggestion}
                displayTransform={(_id, display) => `@${display}`}
                appendSpaceOnAdd
              />
            }
          </MentionsInput>
        </div>

        <EmojiPicker onEmojiSelect={addEmojiFromPicker}/>
      </div>

      <div className="d-flex mt-3">
        {editing &&
          <Button
            className={classNames(button, "ml-auto")}
            color="secondary"
            disabled={submitting}
            onClick={onCancel}
            data-test-id="cancel-button"
          >
            Cancel
          </Button>
        }
        <Button
          className={classNames(button, editing ? "ml-2" : "ml-auto")}
          onClick={submit}
          color="primary"
          style={{ backgroundColor: colorBlue50, color: "white", border: 0 }}
          disabled={submitting || !text.trim()} // not allow whitespace-only text
          data-test-id="submit-button"
        >
          {buttonText}
        </Button>
      </div>
    </Form>
  );
};

CommentForm.defaultProps = {
  editing: false,
  initialText: "",
  focus: false
};

CommentForm.propTypes = {
  buttonText: PropTypes.string,
  editing: PropTypes.bool,
  hashid: PropTypes.string,
  initialText: PropTypes.string,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  placeholder: PropTypes.string,
  userMentionsData: PropTypes.func,
  focus: PropTypes.bool
};

export default CommentForm;
